import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useI18nContext } from "@hopper-b2b/i18n";
import { SliceStopCountFilter } from "@hopper-b2b/types";
import { Box, Button } from "@material-ui/core";
import { ClientContext } from "../../../../../App";
import {
  getFareclassOptionFilter,
  getHasSetStopsOption,
  getHasSetDepartureTimeRange,
  getHasSetArrivalTimeRange,
  getHasSetFareClassFilter,
  getHasSetAirlineFilter,
  getStopsOption,
  getMaxPriceFilter,
} from "../../../../search/reducer/selectors";
import { FARE_CLASS_I18N_KEY } from "../../../components/FlightShopHeader/components/FareClassOptionSelection/constants";
import {
  sortOptionSelector,
  hasSetMaxPriceFilterSelectorV2,
  allFlightShopFilterSelector,
} from "../../../reducer/selectors";
import type { HorizontalFilterBarProps, FilterChipData } from "./types";
import { FilterType } from "./types";
import "./styles.scss";

export const HorizontalFilterBar = ({
  handleOpenFilters,
}: HorizontalFilterBarProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const clientContext = useContext(ClientContext);
  const fareClassFilter = useSelector(getFareclassOptionFilter);
  const currentSortOption = useSelector(sortOptionSelector);

  // Filter values for dynamic labels
  const currentStopsOption = useSelector(getStopsOption);
  const currentMaxPrice = useSelector(getMaxPriceFilter);
  const flightShopFilters = useSelector(allFlightShopFilterSelector);
  const availableMaxPrice = flightShopFilters.priceMax;

  // Filter active states
  const isStopsActive = useSelector(getHasSetStopsOption);
  const isDepartureTimeActive = useSelector(getHasSetDepartureTimeRange);
  const isArrivalTimeActive = useSelector(getHasSetArrivalTimeRange);
  const isPriceActive = useSelector(hasSetMaxPriceFilterSelectorV2);
  const isFareActive = useSelector(getHasSetFareClassFilter);
  const isAirlineActive = useSelector(getHasSetAirlineFilter);

  // Computed active states
  const isTimesActive = isDepartureTimeActive || isArrivalTimeActive;
  const isSortActive =
    currentSortOption !== "fareScore" &&
    currentSortOption !== "stopAndDepartureTime";

  // Check if any filter is active
  const hasAnyActiveFilter =
    isSortActive ||
    isStopsActive ||
    isTimesActive ||
    isPriceActive ||
    isFareActive ||
    isAirlineActive;

  const chips: FilterChipData[] = useMemo(() => {
    // Get current fare class for label
    const activeFareClass = Object.entries(fareClassFilter).find(
      ([_, isActive]) => isActive
    )?.[0] as keyof typeof FARE_CLASS_I18N_KEY | undefined;

    const fareLabel = activeFareClass
      ? `${t("searchFilter.fareClass")}: ${t(FARE_CLASS_I18N_KEY[activeFareClass])}`
      : t("searchFilter.fareClass");

    // Map current sort option to display label
    const sortOptionLabels: Record<string, string> = {
      fareScore: t("searchFilter.sortLabel.recommended"),
      stopAndDepartureTime: t("searchFilter.sortLabel.recommended"),
      price: t("searchFilter.sortLabel.price"),
      departureTime: t("searchFilter.sortLabel.departureTime"),
      arrivalTime: t("searchFilter.sortLabel.arrivalTime"),
      stops: t("searchFilter.sortLabel.stops"),
      duration: t("searchFilter.sortLabel.duration"),
    };

    // Show "By [option]" format when sort is active, otherwise show regular label
    const sortLabel = isSortActive
      ? sortOptionLabels[currentSortOption] ||
        t("searchFilter.sortLabel.recommended")
      : t("sortOption.recommended");

    const priceLabel = isPriceActive
      ? t("searchFilter.maxPriceLabel", {
          price: formatFiatCurrency(
            { ...availableMaxPrice, value: currentMaxPrice },
            { maximumFractionDigits: 0, minimumFractionDigits: 0 }
          ),
        })
      : t("searchFilter.price");

    // Show specific stop count when stops filter is active
    const stopsLabel = isStopsActive
      ? {
          [SliceStopCountFilter.NONE]: t("searchFilter.noStops"),
          [SliceStopCountFilter.ONE_OR_LESS]: t("searchFilter.oneOrFewerStops"),
          [SliceStopCountFilter.TWO_OR_LESS]: t("searchFilter.twoOrFewerStops"),
        }[currentStopsOption] || t("searchFilter.stops")
      : t("searchFilter.stops");

    const allChips: FilterChipData[] = [
      {
        key: FilterType.Sort,
        label: sortLabel,
        isActive: isSortActive,
      },
      {
        key: FilterType.Stops,
        label: stopsLabel,
        isActive: isStopsActive,
      },
      {
        key: FilterType.Times,
        label: t("searchFilter.time"),
        isActive: isTimesActive,
      },
      {
        key: FilterType.Price,
        label: priceLabel,
        isActive: isPriceActive,
      },
      {
        key: FilterType.Fare,
        label: fareLabel,
        isActive: isFareActive,
      },
      {
        key: FilterType.Airline,
        label: t("searchFilter.airline"),
        isActive: isAirlineActive,
      },
    ];

    // Sort to show active filters first
    return allChips.sort((a, b) => {
      if (a.isActive === b.isActive) return 0;
      return a.isActive ? -1 : 1;
    });
  }, [
    fareClassFilter,
    currentSortOption,
    isSortActive,
    isStopsActive,
    isTimesActive,
    isPriceActive,
    isFareActive,
    isAirlineActive,
    currentStopsOption,
    currentMaxPrice,
    availableMaxPrice,
    formatFiatCurrency,
    t,
  ]);

  return (
    <Box className="horizontal-filter-bar">
      <Box className="horizontal-filter-bar__scroll-wrapper">
        <Box className="horizontal-filter-bar__chips-container">
          <Button
            className="horizontal-filter-bar__filter-button"
            data-active={hasAnyActiveFilter}
            onClick={() => handleOpenFilters()}
            aria-label={
              hasAnyActiveFilter ? `${t("filter")} (active)` : t("filter")
            }
          >
            {clientContext?.assets?.filter && (
              <img
                src={clientContext.assets.filter}
                alt=""
                className="horizontal-filter-bar__filter-icon"
              />
            )}
          </Button>
          {chips.map((chip) => (
            <Button
              key={chip.key}
              className="horizontal-filter-bar__filter-chip"
              data-active={chip.isActive}
              onClick={() => handleOpenFilters(chip.key)}
              aria-label={chip.isActive ? `${chip.label} (active)` : chip.label}
            >
              {chip.label}
              {chip.isActive && (
                <FontAwesomeIcon
                  icon={faChevronDown as IconProp}
                  className="horizontal-filter-bar__chip-chevron"
                />
              )}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
